// client/src/components/pages/Geofencing/zoneEditor/zoneFormat.ts
// The zone editor's edges to the outside world (ST-00239), all pure:
//   - stored geofence rows ([lat, lng] pairs / centre + radius) <-> editor shapes;
//   - GeoJSON / KML / pasted-corner-list import (one shape is picked from many);
//   - GeoJSON export;
//   - the search box's coordinate parsing (decimal, DMS, Google Maps links).
import type { GeofenceRow, LatLngPair } from '../../../../types/clientPages/geofencing';
import {
  circleRing,
  dedupeRing,
  isFiniteLngLat,
  roundCoord,
  shapesEqual,
  type LngLat,
  type ZoneShape,
} from './zoneGeometry';

// ---------- stored rows <-> shapes ----------

/** Stored `Coordinates` (JSON text or array) as [lat, lng] pairs, or null. */
export function parseStoredCoordinates(value: GeofenceRow['Coordinates']): LatLngPair[] | null {
  let raw: unknown = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(raw)) return null;
  const pairs = raw
    .filter((c): c is unknown[] => Array.isArray(c) && c.length >= 2)
    .map((c): LatLngPair => [Number(c[0]), Number(c[1])])
    .filter(([lat, lng]) => isFiniteLngLat([lng, lat]));
  return pairs.length ? pairs : null;
}

export const isPolygonType = (geofenceType: string | null | undefined): boolean =>
  geofenceType === 'POLYGON';

/**
 * The editor shape for a saved zone, deciding polygon vs circle exactly as the
 * server and engine do (POLYGON with >= 3 corners, else centre + radius).
 * Legacy rows say 'CIRCULAR' or 'CIRCLE'; both are circles.
 */
export function shapeFromGeofence(row: GeofenceRow | null | undefined): ZoneShape | null {
  if (!row) return null;
  const pairs = parseStoredCoordinates(row.Coordinates);
  if (isPolygonType(row.GeofenceType) && pairs && pairs.length >= 3) {
    const ring = pairs.map(([lat, lng]): LngLat => [lng, lat]);
    const first = ring[0]!;
    const last = ring[ring.length - 1]!;
    if (ring.length > 3 && first[0] === last[0] && first[1] === last[1]) ring.pop();
    return { kind: 'polygon', ring };
  }
  const lat = row.Latitude == null ? NaN : Number(row.Latitude);
  const lng = row.Longitude == null ? NaN : Number(row.Longitude);
  if (!isFiniteLngLat([lng, lat])) return null;
  const r = Number(row.RadiusMeters);
  return { kind: 'circle', center: [lng, lat], radiusM: Number.isFinite(r) && r > 0 ? r : 500 };
}

/** The geometry part of the save payload, in the shape the server has always accepted. */
export interface GeometryFields {
  geofenceType: string;
  coordinates?: LatLngPair[] | null;
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
}

/**
 * Geometry payload fields for `shape`. An untouched saved shape is sent back
 * exactly as stored (no rounding churn, so a name-only edit makes no geometry
 * change). A saved polygon turned into a circle clears its old corners.
 */
export function geometryFields(
  shape: ZoneShape,
  saved: GeofenceRow | null | undefined,
  savedShape: ZoneShape | null
): GeometryFields {
  const unchanged = !!saved && shapesEqual(shape, savedShape);
  if (shape.kind === 'polygon') {
    const stored = unchanged ? parseStoredCoordinates(saved!.Coordinates) : null;
    return {
      geofenceType: 'POLYGON',
      coordinates:
        stored ?? shape.ring.map(([lng, lat]): LatLngPair => [roundCoord(lat), roundCoord(lng)]),
    };
  }
  const savedType = saved?.GeofenceType;
  const fields: GeometryFields = {
    geofenceType: savedType && !isPolygonType(savedType) ? savedType : 'CIRCULAR',
    latitude: unchanged ? Number(saved!.Latitude) : roundCoord(shape.center[1]),
    longitude: unchanged ? Number(saved!.Longitude) : roundCoord(shape.center[0]),
    radiusMeters: unchanged ? Number(saved!.RadiusMeters) : Math.round(shape.radiusM * 10) / 10,
  };
  if (saved && isPolygonType(saved.GeofenceType)) fields.coordinates = null;
  return fields;
}

// ---------- export ----------

export interface ZoneFeature {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: { type: 'Polygon'; coordinates: LngLat[][] };
}

/**
 * The zone as a GeoJSON Feature. Circles go out as a 64-corner polygon (every
 * GIS tool reads that) with `shape: 'circle'`, `center` and `radius_m`, so an
 * import of the same file gives the exact circle back.
 */
export function shapeToFeature(
  shape: ZoneShape,
  props: { name?: string; description?: string } = {}
): ZoneFeature {
  const ring =
    shape.kind === 'circle' ? circleRing(shape.center, shape.radiusM, 64) : shape.ring.slice();
  const closed = [...ring.map((p): LngLat => [roundCoord(p[0]), roundCoord(p[1])])];
  closed.push([closed[0]![0], closed[0]![1]]);
  const properties: Record<string, unknown> = {};
  if (props.name) properties.name = props.name;
  if (props.description) properties.description = props.description;
  if (shape.kind === 'circle') {
    properties.shape = 'circle';
    properties.center = [roundCoord(shape.center[0]), roundCoord(shape.center[1])];
    properties.radius_m = Math.round(shape.radiusM * 10) / 10;
  }
  return { type: 'Feature', properties, geometry: { type: 'Polygon', coordinates: [closed] } };
}

/** A safe download file name for a zone. */
export function exportFileName(name: string | null | undefined): string {
  const base = (name || 'zone')
    .trim()
    .replace(/[^\w\- ]+/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 60);
  return `${base || 'zone'}.geojson`;
}

// ---------- import ----------

export interface ImportCandidate {
  id: string;
  name: string;
  shape: ZoneShape;
  /** Why the shape differs from the source (holes dropped, ring closed, ...). */
  notes: string[];
}

export interface ImportResult {
  candidates: ImportCandidate[];
  /** Things skipped in the source (points without a radius, open lines, ...). */
  skipped: string[];
  /** Set when nothing usable was found. */
  error: string | null;
}

type Json = unknown;
const isObj = (v: Json): v is Record<string, Json> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

function nameFrom(props: Json, fallback: string): string {
  if (isObj(props)) {
    for (const key of ['name', 'Name', 'NAME', 'title', 'label', 'zone', 'Zone']) {
      const v = props[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
      if (typeof v === 'number') return String(v);
    }
  }
  return fallback;
}

class ImportCollector {
  candidates: ImportCandidate[] = [];
  skipped: string[] = [];
  outOfRange = false;
  private count = 0;

  /** A GeoJSON-order ring (closed or open) -> a polygon candidate. */
  addRing(rawRing: Json, name: string, notes: string[] = []): void {
    if (!Array.isArray(rawRing)) return;
    const pts: LngLat[] = [];
    for (const c of rawRing) {
      if (!Array.isArray(c) || c.length < 2) continue;
      const p: LngLat = [Number(c[0]), Number(c[1])];
      if (!isFiniteLngLat(p)) {
        if (Number.isFinite(p[0]) && Number.isFinite(p[1])) this.outOfRange = true;
        return;
      }
      pts.push(p);
    }
    const ring = dedupeRing(pts);
    if (ring.length < 3) {
      this.skipped.push(`${name}: fewer than 3 corners`);
      return;
    }
    this.count += 1;
    this.candidates.push({
      id: `c${this.count}`,
      name,
      shape: { kind: 'polygon', ring },
      notes,
    });
  }

  addCircle(center: LngLat, radiusM: number, name: string): void {
    if (!isFiniteLngLat(center) || !(radiusM > 0)) return;
    this.count += 1;
    this.candidates.push({
      id: `c${this.count}`,
      name,
      shape: { kind: 'circle', center: [center[0], center[1]], radiusM },
      notes: [],
    });
  }
}

const radiusFrom = (props: Json): number | null => {
  if (!isObj(props)) return null;
  for (const key of ['radius_m', 'radiusMeters', 'radius', 'RadiusMeters', 'Radius']) {
    const v = Number(props[key]);
    if (Number.isFinite(v) && v > 0) return v;
  }
  return null;
};

function collectGeometry(geom: Json, props: Json, name: string, out: ImportCollector): void {
  if (!isObj(geom)) return;
  const type = geom.type;
  const coords = geom.coordinates;
  if (type === 'Polygon' && Array.isArray(coords)) {
    // Our own export of a circle: rebuild the exact circle.
    if (isObj(props) && props.shape === 'circle') {
      const c = props.center;
      const r = radiusFrom(props);
      if (Array.isArray(c) && r) {
        out.addCircle([Number(c[0]), Number(c[1])], r, name);
        return;
      }
    }
    const notes = coords.length > 1 ? ['Holes inside the shape were left out.'] : [];
    out.addRing(coords[0], name, notes);
  } else if (type === 'MultiPolygon' && Array.isArray(coords)) {
    coords.forEach((poly, k) => {
      if (!Array.isArray(poly)) return;
      const notes = poly.length > 1 ? ['Holes inside the shape were left out.'] : [];
      out.addRing(poly[0], coords.length > 1 ? `${name} (part ${k + 1})` : name, notes);
    });
  } else if (type === 'Point' && Array.isArray(coords)) {
    const r = radiusFrom(props);
    if (r) out.addCircle([Number(coords[0]), Number(coords[1])], r, name);
    else out.skipped.push(`${name}: a point without a radius`);
  } else if (type === 'LineString' && Array.isArray(coords)) {
    const first = coords[0];
    const last = coords[coords.length - 1];
    const closed =
      coords.length >= 4 &&
      Array.isArray(first) &&
      Array.isArray(last) &&
      Number(first[0]) === Number(last[0]) &&
      Number(first[1]) === Number(last[1]);
    if (closed) out.addRing(coords, name, ['A closed line was turned into a shape.']);
    else out.skipped.push(`${name}: an open line (a zone needs an area)`);
  } else if (type === 'MultiLineString' || type === 'MultiPoint') {
    out.skipped.push(`${name}: ${String(type)} cannot be a zone`);
  } else if (type === 'GeometryCollection' && Array.isArray(geom.geometries)) {
    geom.geometries.forEach((g, k) => collectGeometry(g, props, `${name} (${k + 1})`, out));
  }
}

function parseGeoJson(data: Json, out: ImportCollector): void {
  if (!isObj(data)) return;
  if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
    data.features.forEach((f, i) => {
      if (isObj(f))
        collectGeometry(f.geometry, f.properties, nameFrom(f.properties, `Shape ${i + 1}`), out);
    });
  } else if (data.type === 'Feature') {
    collectGeometry(data.geometry, data.properties, nameFrom(data.properties, 'Shape 1'), out);
  } else {
    collectGeometry(data, null, 'Shape 1', out);
  }
}

const kmlCoordinates = (text: string | null | undefined): number[][] =>
  (text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.split(',').map(Number));

function childText(el: Element, local: string): string | null {
  for (const c of Array.from(el.children)) {
    if (c.localName === local) return c.textContent;
  }
  return null;
}

function parseKml(text: string, out: ImportCollector): string | null {
  if (typeof DOMParser === 'undefined') return 'This browser cannot read KML files.';
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) return 'The KML file could not be read.';
  const placemarks = Array.from(doc.getElementsByTagNameNS('*', 'Placemark'));
  const roots: Array<{ el: Element; name: string }> = placemarks.length
    ? placemarks.map((p, i) => ({
        el: p,
        name: (childText(p, 'name') || '').trim() || `Shape ${i + 1}`,
      }))
    : [{ el: doc.documentElement, name: 'Shape 1' }];
  for (const { el, name } of roots) {
    const polygons = Array.from(el.getElementsByTagNameNS('*', 'Polygon'));
    polygons.forEach((poly, k) => {
      const outer = poly.getElementsByTagNameNS('*', 'outerBoundaryIs')[0];
      const coordsEl = outer?.getElementsByTagNameNS('*', 'coordinates')[0];
      const holes = poly.getElementsByTagNameNS('*', 'innerBoundaryIs').length;
      out.addRing(
        kmlCoordinates(coordsEl?.textContent),
        polygons.length > 1 ? `${name} (part ${k + 1})` : name,
        holes ? ['Holes inside the shape were left out.'] : []
      );
    });
    if (!polygons.length) {
      const line = el.getElementsByTagNameNS('*', 'LineString')[0];
      if (line) {
        const coords = kmlCoordinates(
          line.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent
        );
        const first = coords[0];
        const last = coords[coords.length - 1];
        if (coords.length >= 4 && first && last && first[0] === last[0] && first[1] === last[1]) {
          out.addRing(coords, name, ['A closed line was turned into a shape.']);
        } else {
          out.skipped.push(`${name}: an open line (a zone needs an area)`);
        }
      } else if (el.getElementsByTagNameNS('*', 'Point').length) {
        out.skipped.push(`${name}: a point without a radius`);
      }
    }
  }
  return null;
}

/** Lines of "lat, lng" (or "lat lng") pairs — corners copied from a spreadsheet. */
function parseCornerList(text: string, out: ImportCollector): boolean {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 3) return false;
  const pts: number[][] = [];
  for (const line of lines) {
    const m = line.match(/^(-?\d+(?:\.\d+)?)\s*[,;\t ]\s*(-?\d+(?:\.\d+)?)$/);
    if (!m) return false;
    pts.push([Number(m[2]), Number(m[1])]); // lat, lng -> [lng, lat]
  }
  out.addRing(pts, 'Pasted corners');
  return true;
}

/**
 * Every shape in a pasted or uploaded GeoJSON / KML text, or a list of
 * "lat, lng" corners (one per line). Holes and extra parts become notes or
 * separate candidates; the caller lets the user pick one.
 */
export function parseZoneImport(text: string): ImportResult {
  const out = new ImportCollector();
  const trimmed = (text || '').replace(/^﻿/, '').trim();
  if (!trimmed)
    return { candidates: [], skipped: [], error: 'Paste GeoJSON or KML, or choose a file.' };
  let error: string | null = null;
  if (trimmed.startsWith('<')) {
    error = parseKml(trimmed, out);
  } else if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      parseGeoJson(JSON.parse(trimmed), out);
    } catch {
      error = 'That is not valid GeoJSON (the JSON could not be read).';
    }
  } else if (!parseCornerList(trimmed, out)) {
    error = 'Paste GeoJSON, KML, or one "latitude, longitude" corner per line.';
  }
  if (!error && !out.candidates.length) {
    error = out.outOfRange
      ? 'The coordinates are not longitude/latitude in degrees (WGS 84). Export the file in WGS 84 and try again.'
      : 'No shape with an area was found. Lines and points cannot be zones.';
  }
  return { candidates: out.candidates, skipped: out.skipped, error };
}

// ---------- search box: coordinates ----------

type Hemi = 'N' | 'S' | 'E' | 'W';

function parseComponent(text: string): { value: number; hemi: Hemi | null } | null {
  const hemiMatch = text.match(/[NSEW]/);
  const hemi = (hemiMatch ? hemiMatch[0] : null) as Hemi | null;
  const nums = (text.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (!nums.length || nums.length > 3) return null;
  const [d, m = 0, s = 0] = nums as [number, number?, number?];
  if (nums.length > 1 && (m >= 60 || s >= 60)) return null;
  let value = d + m / 60 + s / 3600;
  const negative = /-\s*\d/.test(text) || hemi === 'S' || hemi === 'W';
  if (negative) value = -value;
  return { value, hemi };
}

/**
 * A pasted position as [lng, lat], or null. Accepts "lat, lng" decimals
 * ("-26.2041, 28.0473", "-26.2041 28.0473", decimal commas "-26,2041 28,0473"),
 * hemispheres ("26.2041 S 28.0473 E"), degrees-minutes-seconds
 * (26°12'14.8"S 28°02'50.3"E) and Google Maps links (".../@-26.2041,28.0473,15z").
 */
export function parseCoordinateQuery(input: string): LngLat | null {
  if (!input) return null;
  const link =
    input.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/) ||
    input.match(/[?&](?:q|ll|query|center)=(-?\d+(?:\.\d+)?)(?:,|%2C)\s*(-?\d+(?:\.\d+)?)/i);
  if (link) {
    const p: LngLat = [Number(link[2]), Number(link[1])];
    return isFiniteLngLat(p) ? p : null;
  }
  let s = input
    .toUpperCase()
    .replace(/[′’‘`´]/g, "'")
    .replace(/[″“”]/g, '"')
    .replace(/''/g, '"')
    .replace(/º/g, '°')
    .replace(/[−–—]/g, '-')
    .trim();
  if (!/\d/.test(s)) return null;
  if (/[^0-9NSEW°'".,;\-\s]/.test(s)) return null;
  // Decimal commas: exactly two "12,34" numbers separated by whitespace or a semicolon.
  const dc = s.match(/^(-?\d+,\d+)\s*[;\s]\s*(-?\d+,\d+)$/);
  if (dc) s = `${dc[1]!.replace(',', '.')} ${dc[2]!.replace(',', '.')}`;

  let parts: string[];
  if (/[NSEW]/.test(s)) {
    const leading = /^[NSEW]/.test(s);
    parts = leading
      ? s
          .split(/(?=[NSEW])/)
          .map((p) => p.replace(/[,;]/g, ' ').trim())
          .filter(Boolean)
      : (s.match(/[^NSEW]*[NSEW]/g) || []).map((p) => p.replace(/[,;]/g, ' ').trim());
  } else if (/[,;]/.test(s)) {
    parts = s.split(/[,;]/).map((p) => p.trim());
  } else if (/°/.test(s)) {
    parts = s.split(/\s+(?=-?\d+(?:\.\d+)?\s*°)/).map((p) => p.trim());
  } else {
    const nums = s.split(/\s+/).filter(Boolean);
    const half = nums.length / 2;
    if (!Number.isInteger(half) || half < 1 || half > 3) return null;
    parts = [nums.slice(0, half).join(' '), nums.slice(half).join(' ')];
  }
  if (parts.length !== 2) return null;
  const a = parseComponent(parts[0]!);
  const b = parseComponent(parts[1]!);
  if (!a || !b) return null;
  let lat: number;
  let lng: number;
  if (a.hemi === 'E' || a.hemi === 'W' || b.hemi === 'N' || b.hemi === 'S') {
    lat = b.value;
    lng = a.value;
  } else {
    lat = a.value;
    lng = b.value;
    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) [lat, lng] = [lng, lat];
  }
  const p: LngLat = [lng, lat];
  return isFiniteLngLat(p) ? p : null;
}

/** "−26.20410, 28.04730" (lat, lng; 5 decimals ≈ 1 m). */
export function formatLatLng(p: LngLat): string {
  return `${p[1].toFixed(5)}, ${p[0].toFixed(5)}`;
}
